/**
 * UI Component Types
 * 
 * Centralized type definitions for UI components to ensure type safety and consistency.
 * These types define the valid values for component properties and configurations.
 * 
 * @remarks
 * ## Purpose
 * 
 * - ✅ Provide type safety for component inputs
 * - ✅ Centralize type definitions to avoid duplication
 * - ✅ Make refactoring easier (change type in one place)
 * - ✅ Improve IDE autocomplete and IntelliSense
 * - ✅ Document valid values for component properties
 * 
 * ## Usage
 * 
 * @examplex|
 * ```typescript
 * import { FileDownloaderType } from 'app/core/types';
 * 
 * @Component({ ... })
 * export class MyComponent {
 *   @Input() type: FileDownloaderType = 'button';
 * }
 * ```
 * 
 * ## Best Practices
 * 
 * 1. **Use const assertions** for literal types when possible
 * 2. **Export both the type and the values** for runtime use
 * 3. **Document each type** with JSDoc comments
 * 4. **Group related types** together
 * 
 * ## Performance
 * 
 * - Types are compile-time only (zero runtime cost)
 * - No impact on bundle size
 * - No performance overhead
 */

/**
 * Valid types for the file downloader component
 * 
 * @remarks
 * - `button`: Renders as a clickable button element
 * - `link`: Renders as a hyperlink element
 */
export type FileDownloaderType = 'button' | 'link';

/**
 * Button type for the file downloader component
 */
export const FILE_DOWNLOADER_TYPE_BUTTON: FileDownloaderType = 'button';

/**
 * Link type for the file downloader component
 */
export const FILE_DOWNLOADER_TYPE_LINK: FileDownloaderType = 'link';

/**
 * All valid FileDownloaderType values
 * Useful for validation, iteration, or dropdown options
 */
export const FILE_DOWNLOADER_TYPES: readonly FileDownloaderType[] = [
    FILE_DOWNLOADER_TYPE_BUTTON,
    FILE_DOWNLOADER_TYPE_LINK,
] as const;
